import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ConsentContext = createContext();

const CONSENT_COOKIE_NAME = 'tikt_consent';
const CONSENT_VERSION = '1';

const defaultConsent = {
  necessary: true, // Always required
  analytics: false,
  marketing: false,
  preferences: false,
};

function getStoredConsent() {
  try {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));

    if (cookie) {
      const data = JSON.parse(decodeURIComponent(cookie.split('=')[1]));
      if (data.version === CONSENT_VERSION) {
        return data.consent;
      }
    }
  } catch (e) {
    if (import.meta.env.DEV) console.error('Error parsing consent cookie:', e);
  }
  return null;
}

// Save consent to cookie (1 year expiry)
function saveConsent(consent) {
  const data = {
    version: CONSENT_VERSION,
    consent,
    timestamp: new Date().toISOString()
  };

  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);

  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`;
}

// Update Google Consent Mode
function updateGoogleConsent(consent) {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'ad_storage': consent.marketing ? 'granted' : 'denied',
      'ad_user_data': consent.marketing ? 'granted' : 'denied',
      'ad_personalization': consent.marketing ? 'granted' : 'denied',
      'analytics_storage': consent.analytics ? 'granted' : 'denied',
      'functionality_storage': consent.preferences ? 'granted' : 'denied',
      'personalization_storage': consent.preferences ? 'granted' : 'denied',
      'security_storage': 'granted', // Always granted for security purposes
    });
  }
}

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(defaultConsent);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize consent on mount
  useEffect(() => {
    const storedConsent = getStoredConsent();

    if (storedConsent) {
      setConsent(storedConsent);
      updateGoogleConsent(storedConsent);
      setShowBanner(false);
    } else {
      // No stored consent - show banner
      setShowBanner(true);
    }

    setIsInitialized(true);
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };

    setConsent(newConsent);
    saveConsent(newConsent);
    updateGoogleConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Reject all optional cookies
  const rejectAll = useCallback(() => {
    const newConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };

    setConsent(newConsent);
    saveConsent(newConsent);
    updateGoogleConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Save custom preferences
  const savePreferences = useCallback((preferences) => {
    const newConsent = {
      ...preferences,
      necessary: true, // Always required
    };

    setConsent(newConsent);
    saveConsent(newConsent);
    updateGoogleConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Open preferences modal
  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  // Close preferences modal
  const closePreferences = useCallback(() => {
    setShowPreferences(false);
  }, []);

  // Check if a specific consent type is granted
  const hasConsent = useCallback((type) => {
    return consent[type] === true;
  }, [consent]);

  const value = {
    consent,
    showBanner,
    showPreferences,
    isInitialized,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    hasConsent,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return context;
}
